import { useNavigate } from 'react-router-dom'

import PositionDoughnutChart from '@Features/position/PositionDoughnutChart'
import ProductPositionIcon from '@Icons/product-position-icon.svg?react'
import { PositionMap } from '@Store/types'
import type { PositionStatus } from '@Store/types/dashboard'
import Mixpanel from '@Utils/mixpanel/Mixpanel'

export default function ReportDetail({
  position,
  isCheap,
  categoryId,
  brandId,
  view,
}: {
  position: PositionStatus | undefined
  isCheap: number
  categoryId?: string
  brandId?: string
  view?: string
}): JSX.Element {
  const navigate = useNavigate()

  const sendEvent = (event: string): void => {
    Mixpanel.track(event, {
      page: view,
      section: 'Price Position Card',
    })
  }

  return (
    <div className="mt-5 lg:mt-0 border border-solid border-gray-200 rounded-lg shadow flex xl:w-2/3 w-full">
      <div className="flex flex-col px-4 py-5 lg:p-6 h-full w-full">
        <div className="flex flex-row space-x-3 items-center">
          <ProductPositionIcon />
          <h2 className="font-semibold text-xl text-gray-900">
            Price Position
          </h2>
          <div className="flex justify-center items-center py-0.5 px-2 border-0 rounded-2xl bg-error-50">
            <span className="font-medium text-xs text-error-700">
              {PositionMap[isCheap]}
            </span>
          </div>
        </div>
        <div className="flex flex-row space-x-4 h-full items-center">
          <div className="flex items-center w-20 h-20 lg:w-28 lg:h-28 ">
            <PositionDoughnutChart position={position} showLabels={false} />
          </div>

          <div className="w-full flex flex-col space-y-1 mt-4 lg:mt-0">
            <div className="flex flex-col lg:flex-row lg:space-x-4 w-full">
              <button
                onClick={() => {
                  sendEvent('Highest price Clicked')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=5`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=5`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-orange-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  Highest price
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.highest} products
                </span>
              </button>
              <button
                onClick={() => {
                  sendEvent('Cheaper than av.')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=2`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=2`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  Cheaper than av.
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.cheaper} products
                </span>
              </button>
            </div>
            <div className="flex flex-col lg:flex-row lg:space-x-4 w-full">
              <button
                onClick={() => {
                  sendEvent('Average Clicked')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=3`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=3`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-violet-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  Average
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.average} products
                </span>
              </button>
              <button
                onClick={() => {
                  sendEvent('Higher than av. Clicked')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=4`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=4`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  Higher than av.
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.higher} products
                </span>
              </button>
            </div>
            <div className="flex flex-col lg:flex-row lg:space-x-4 w-full">
              <button
                onClick={() => {
                  sendEvent('Cheapest Clicked')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=1`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=1`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-cyan-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  Cheapest
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.cheapest} products
                </span>
              </button>
              <button
                onClick={() => {
                  sendEvent('All equal Clicked')
                  if (categoryId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&category=${categoryId}&is_cheap=6`,
                    )
                  }
                  if (brandId) {
                    navigate(
                      `/products?page=1&limit=10&orderBy=name&orderDirection=asc&brand=${brandId}&is_cheap=6`,
                    )
                  }
                }}
                className="flex flex-row space-x-2 items-center lg:w-1/2"
              >
                <div className="flex">
                  <div className="w-2.5 h-2.5 bg-rose-400 rounded-full"></div>
                </div>

                <span className="font-medium text-sm text-gray-500">
                  All equal
                </span>
                <span className="font-normal text-sm text-gray-900">
                  {position?.allequal} products
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
